import { makeAutoObservable, runInAction } from 'mobx';
import type { ShiftHistoryItem } from '../types';
import { shiftHistoryApi } from '../api/shift_history_api';

export class ShiftHistoryStore {
  items: ShiftHistoryItem[] = [];
  loading = false;
  cursor: string | null = null;
  hasMore = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetch shift history for support team
   */
  async fetchBySupport(refresh = false): Promise<void> {
    if (this.loading) return;
    if (!refresh && !this.hasMore) return;

    runInAction(() => {
      this.loading = true;
      this.error = null;
      if (refresh) {
        this.cursor = null;
        this.hasMore = true;
      }
    });

    try {
      const cursorParam = refresh ? undefined : this.cursor || undefined;
      const response = await shiftHistoryApi.listBySupport(cursorParam);

      runInAction(() => {
        if (refresh) {
          this.items = response.items;
        } else {
          this.items = [...this.items, ...response.items];
        }
        this.cursor = response.cursor;
        this.hasMore = response.has_more;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Failed to fetch shift history';
      });
    }
  }

  /**
   * Receive real-time update from Socket.IO
   */
  receiveUpdate(item: ShiftHistoryItem): void {
    runInAction(() => {
      // Add new item at the beginning (most recent first)
      // Check for duplicates by uuid
      const exists = this.items.some((i) => i.uuid === item.uuid);
      if (!exists) {
        this.items = [item, ...this.items];
      }
    });
  }

  /**
   * Clear the store
   */
  clear(): void {
    this.items = [];
    this.cursor = null;
    this.hasMore = true;
    this.error = null;
  }
}

export const shiftHistoryStore = new ShiftHistoryStore();
